import { useMemo } from 'react';
import { cn } from 'utils/helpers';
import type { QuoteTableColumn } from '../../../../../components/QuoteTable/quote-table.types';
import { CELL_BORDER, NUMERIC_COLUMN_TYPES } from '../../../../../components/QuoteTable/quote-table.types';
import { QuoteSummaryTotals } from '../../../../../components/QuoteTable/QuoteSummaryTotals';
import type { SummaryTotalRow } from '../../../../../components/QuoteTable/QuoteSummaryTotals';
import { QuoteTableTotalsRow } from '../../../../../components/QuoteTable/QuoteTableTotalsRow';
import { FiUser } from 'react-icons/fi';

export interface ReadOnlyCellValue {
	columnId: number;
	displayValue: string;
}

export interface ReadOnlyRow {
	cells: ReadOnlyCellValue[];
	isSupplierRow?: boolean;
	rowId: number;
}

export interface ReadOnlyQuoteTableProps {
	className?: string;
	columns: QuoteTableColumn[];
	columnTotals?: Record<number, number> | null;
	diffKeys?: Set<string>;
	hoveredRowIndex?: number | null;
	muted?: boolean;
	rows: ReadOnlyRow[];
	totals?: SummaryTotalRow[];
	onRowHover?: (index: number | null) => void;
}

const DIFF_HIGHLIGHT = 'bg-blue-50 font-medium text-blue-900 ring-2 ring-inset ring-blue-300';
const SUPPLIER_ROW_BG = 'bg-blue-100/30';

const ReadOnlyQuoteTable: React.FC<ReadOnlyQuoteTableProps> = ({
	className,
	columns,
	columnTotals,
	rows,
	totals,
	diffKeys,
	hoveredRowIndex,
	muted = false,
	onRowHover,
}) => {
	const sortedColumns = useMemo(
		() => [...columns].sort((a, b) => a.sortOrder - b.sortOrder),
		[columns]
	);

	const textClass = muted ? 'text-gray-500' : 'text-gray-900';
	const headerBg = muted ? 'bg-[#F3F4F6]' : 'bg-gray-200';
	const headerTextClass = muted ? 'text-gray-400' : 'text-tertiary-600';
	const tableBg = muted ? 'bg-gray-50' : 'bg-white';

	const columnWidths = useMemo(() => {
		const widths: Record<number, string> = {};
		for (const col of sortedColumns) {
			widths[col.id] = col.columnType === 'description' ? '25%' : '12%';
		}
		return widths;
	}, [sortedColumns]);

	// Total rows: spacer = # col + all data cols except last two (label + value)
	// Total visible columns = 1 (# col) + sortedColumns.length
	// Spacer = total columns - 2
	const totalSpacerColSpan = Math.max(1, sortedColumns.length - 1);

	return (
		<div
			className="overflow-x-auto"
			data-testid="readonly-quote-table-wrapper"
		>
			<table
				className={cn(
					'w-full table-fixed rounded-xl border-b border-solid border-[#EAECF0]',
					tableBg,
					className
				)}
				style={{ minWidth: `${Math.max(800, (sortedColumns.length + 1) * 100)}px` }}
				data-testid="readonly-quote-table"
			>
				<caption className="sr-only">Quote table (read-only)</caption>

				<thead className={cn('h-[44px]', headerBg)}>
					<tr className="border-b border-[#EAECF0]">
						<th
							className={cn(
								headerBg,
								'font-roboto text-[12px] font-medium text-center',
								headerTextClass
							)}
							style={{ width: '3%' }}
						>
							<div className="mt-[3px]">#</div>
						</th>
						{sortedColumns.map((col) => (
							<th
								key={col.id}
								className={cn(
									headerBg,
									'font-roboto text-[12px] font-medium',
									NUMERIC_COLUMN_TYPES.has(col.columnType) ? 'text-right pr-4' : 'text-left',
									headerTextClass
								)}
								style={{ width: columnWidths[col.id] }}
							>
								<div className={cn(
									'mt-[3px]',
									!NUMERIC_COLUMN_TYPES.has(col.columnType) && 'md:ml-1 lg:ml-1'
								)}>
									{col.label}
								</div>
							</th>
						))}
					</tr>
				</thead>

				<tbody>
					{rows.map((row, idx) => {
						const isHovered = hoveredRowIndex === idx;
						const cellMap = new Map(row.cells.map((c) => [c.columnId, c.displayValue]));

						return (
							<tr
								key={row.rowId}
								className={cn(
									'border-b border-[#EAECF0] transition-colors',
									isHovered && 'bg-blue-100',
									row.isSupplierRow && SUPPLIER_ROW_BG
								)}
								onMouseEnter={() => onRowHover?.(idx)}
								onMouseLeave={() => onRowHover?.(null)}
								data-testid={`readonly-row-${row.rowId}`}
							>
								<td
									className={cn(
										CELL_BORDER,
										'text-center font-roboto text-xs font-normal text-primary-500',
										row.isSupplierRow && SUPPLIER_ROW_BG
									)}
									style={{ height: '35px' }}
								>
									<div className="flex items-center justify-center gap-1">
										<span>{idx + 1}</span>
										{row.isSupplierRow && <FiUser
											className="text-blue-500"
											size={10}
											title="Supplier-added row"
										/>}
									</div>
								</td>
								{sortedColumns.map((col) => {
									const isDiff = diffKeys?.has(`cell-${row.rowId}-${col.id}`) ?? false;
									const isNumeric = NUMERIC_COLUMN_TYPES.has(col.columnType);
									const isSavings = col.columnType === 'saving';

									return (
										<td
											key={col.id}
											className={cn(
												CELL_BORDER,
												'pl-2 font-roboto text-sm',
												!isHovered && !isDiff && 'bg-gray-50',
												isDiff ? DIFF_HIGHLIGHT : textClass,
												isNumeric && 'text-right pr-4',
												isSavings && 'text-red-600',
												row.isSupplierRow && !isHovered && SUPPLIER_ROW_BG
											)}
											data-testid={`readonly-cell-${row.rowId}-${col.id}`}
										>
											{isSavings && "-"}{cellMap.get(col.id) ?? '-'}
										</td>
									);
								})}
							</tr>
						);
					})}

					{totals && totals.length > 0 && (
						<QuoteSummaryTotals
							diffKeys={diffKeys}
							spacerColSpan={totalSpacerColSpan}
							testIdPrefix="readonly"
							totals={totals}
						/>
					)}
				</tbody>

				{columnTotals && (
					<QuoteTableTotalsRow
						columns={columns}
						columnTotals={columnTotals}
					/>
				)}
			</table>
		</div>
	);
};

export { ReadOnlyQuoteTable };
